import React from 'react';
import { IAlertItem, IAssistantMessage, AlertVariant } from '../../../types';
import { FiInfo, FiCheckCircle, FiAlertTriangle, FiXCircle } from 'react-icons/fi';

const VARIANT_STYLES: Record<AlertVariant, {
    container: string;
    icon: React.ReactElement;
    title: string;
    description: string;
    primaryBtn: string;
    secondaryBtn: string;
}> = {
    info: {
        container: 'border-blue-300 bg-blue-50',
        icon: <FiInfo className="w-4 h-4 mt-0.5 shrink-0 text-blue-600" />,
        title: 'text-blue-700',
        description: 'text-blue-600',
        primaryBtn: 'bg-blue-600 text-white hover:bg-blue-700',
        secondaryBtn: 'border border-blue-300 text-blue-600 hover:bg-blue-50',
    },
    success: {
        container: 'border-green-300 bg-green-50',
        icon: <FiCheckCircle className="w-4 h-4 mt-0.5 shrink-0 text-green-600" />,
        title: 'text-green-700',
        description: 'text-green-600',
        primaryBtn: 'bg-green-600 text-white hover:bg-green-700',
        secondaryBtn: 'border border-green-300 text-green-600 hover:bg-green-50',
    },
    warning: {
        container: 'border-amber-300 bg-amber-50',
        icon: <FiAlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />,
        title: 'text-amber-700',
        description: 'text-amber-600',
        primaryBtn: 'bg-amber-600 text-white hover:bg-amber-700',
        secondaryBtn: 'border border-amber-300 text-amber-600 hover:bg-amber-50',
    },
    error: {
        container: 'border-red-300 bg-red-50',
        icon: <FiXCircle className="w-4 h-4 mt-0.5 shrink-0 text-red-600" />,
        title: 'text-red-700',
        description: 'text-red-600',
        primaryBtn: 'bg-red-600 text-white hover:bg-red-700',
        secondaryBtn: 'border border-red-300 text-red-600 hover:bg-red-50',
    },
};

interface AlertMessageBubbleProps {
    message: IAssistantMessage;
    /** Called when the user clicks an action button. Omit to hide buttons. */
    onAction?: (actionId: string) => void;
}

export const AlertMessageBubble: React.FC<AlertMessageBubbleProps> = ({ message, onAction }) => {
    const variant = message.alertVariant ?? 'info';
    const styles = VARIANT_STYLES[variant];

    return (
        <div className="flex justify-start mb-4 items-start">
            {/* Assistant avatar */}
            <div className="flex-shrink-0 w-[39px] h-[39px] rounded-full bg-primary-500 flex items-center justify-center text-white mr-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 8V4H8" />
                    <rect width="16" height="12" x="4" y="8" rx="2" />
                    <path d="M2 14h2" />
                    <path d="M20 14h2" />
                    <path d="M15 13v2" />
                    <path d="M9 13v2" />
                </svg>
            </div>

            {/* Alert card */}
            <div className={`rounded-lg border px-4 py-3 text-sm w-full max-w-[85%] ${styles.container}`}>
                <div className={`flex items-start gap-2 font-semibold ${styles.title}`}>
                    {styles.icon}
                    <span>{message.alertTitle ?? message.message}</span>
                </div>
                {message.alertTitle && message.message && (
                    <p className={`text-xs mt-1 ml-6 ${styles.description}`}>{message.message}</p>
                )}
                {message.alertItems && message.alertItems.length > 0 && (
                    <ul className="mt-2 ml-6 space-y-1">
                        {message.alertItems.map((item: IAlertItem, i: number) => {
                            const isPass = item.statusVariant === 'success';
                            const isInfo = item.statusVariant === 'info';
                            const icon = isPass
                                ? <FiCheckCircle className="w-3.5 h-3.5 shrink-0 text-green-600" />
                                : isInfo
                                    ? <FiInfo className="w-3.5 h-3.5 shrink-0 text-blue-500" />
                                    : <FiXCircle className="w-3.5 h-3.5 shrink-0 text-red-500" />;
                            const textColor = isPass ? 'text-green-700' : isInfo ? 'text-blue-600' : 'text-red-600';
                            return (
                                <li key={i} className={`flex items-center gap-1.5 text-xs ${textColor}`}>
                                    {icon}
                                    <span className="font-medium">{item.label}</span>
                                    {item.detail && <span className="font-normal opacity-80">— {item.detail}</span>}
                                </li>
                            );
                        })}
                    </ul>
                )}
                {message.alertActions && message.alertActions.length > 0 && onAction && (
                    <div className="flex gap-2 mt-3 ml-6">
                        {message.alertActions.map(action => (
                            <button
                                key={action.actionId}
                                onClick={() => onAction(action.actionId)}
                                className={`px-4 py-1.5 rounded text-xs font-semibold ${
                                    action.variant === 'secondary'
                                        ? `bg-white ${styles.secondaryBtn}`
                                        : styles.primaryBtn
                                }`}
                            >
                                {action.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};
